import { CustomControl } from './CustomControl';

export class FloatingControls extends CustomControl {
  constructor(canvasObject, options = {}) {
    const defaultOptions = {
      offsetX: 0,
      offsetY: -60,
      className: 'floating-controls',
      // views: { [id]: { title?, showBack?, backTo?, buttons: [] } }
      // Backwards compatibility: if only buttons are provided, create a default 'home' view
      buttons: [],
      views: undefined,
      initialView: 'home',
      ...options,
    };

    // Call parent constructor
    super(canvasObject, defaultOptions);
  }

  beforeCreateElement() {
    // Initialize properties before createElement is called
    this._activeViewButtonsDom = [];
    this.isEditMode = false;

    // Normalize options to views structure
    if (!this.options.views) {
      this.options.views = {
        home: {
          title: '',
          showBack: false,
          backTo: 'home',
          buttons: this.options.buttons || [],
        },
      };
    }
    this.activeView = this.options.initialView || 'home';
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.className = `${this.options.className} floating-controls-container`;
    this.element.style.position = 'absolute';
    this.element.style.zIndex = this.options.zIndex;
    this.element.style.display = 'none';
    this.element.style.pointerEvents = 'auto';

    // Panel wrapper with unified styling
    const panel = document.createElement('div');
    panel.className = 'floating-controls-panel';
    this.element.appendChild(panel);

    // Header (for title and back button) inside panel
    const header = document.createElement('div');
    header.className = 'floating-controls-header';
    panel.appendChild(header);

    // Content container for buttons inside panel
    const innerContainer = document.createElement('div');
    innerContainer.className = 'floating-controls-inner';
    panel.appendChild(innerContainer);

    // Footer (for back button) inside panel
    const footer = document.createElement('div');
    footer.className = 'floating-controls-footer';
    panel.appendChild(footer);

    this.renderActiveView();
  }

  // Color utilities for injected SVG icons inside buttons
  _setIconColorFor(button, color) {
    if (!button) return;
    const svg = button.querySelector('svg');
    if (!svg) return;
    const nodes = svg.querySelectorAll(
      'path, rect, circle, ellipse, polygon, polyline, line'
    );
    nodes.forEach((el) => {
      try {
        const fillAttr = (el.getAttribute('fill') || '').toLowerCase();
        const hasFillAttr = el.hasAttribute('fill');
        const hasStrokeAttr = el.hasAttribute('stroke');

        // Preserve outline-only icons: never force-fill elements that declare fill="none"
        if (fillAttr === 'none') {
          el.style.fill = 'none';
          // Ensure stroke follows requested color
          el.style.stroke = color;
          return;
        }

        // If element explicitly declares a fill (not 'none'), tint its fill
        if (hasFillAttr) {
          el.style.fill = color;
        }

        // If element has a stroke, tint it as well
        if (hasStrokeAttr) {
          el.style.stroke = color;
        }
      } catch (e) {
        console.error('Failed tinting icon (setIconColorFor)', e);
      }
    });
  }

  _updateIconColorForButton(button) {
    if (!button) return;
    const isDisabled =
      button.classList.contains('disabled') || button.hasAttribute('disabled');
    const isActive =
      button.classList.contains('active') || button.classList.contains('hover');
    const isMenu = button.classList.contains('menu-item');
    const isTextControl = button.classList.contains('btn-text-control');
    // Rules:
    // - Disabled: gray
    // - Submenu items (.menu-item): always black
    // - Text controls (.btn-text-control): keep black even on hover/active
    // - Others (top-level non-text): white on hover/active, black otherwise
    const color = isDisabled
      ? '#94a3b8'
      : isMenu || isTextControl
      ? '#000000'
      : isActive
      ? '#ffffff'
      : '#000000';
    this._setIconColorFor(button, color);
  }

  refreshIconColors() {
    try {
      this._activeViewButtonsDom.forEach((btn) => {
        if (!btn) return;
        if (btn.classList && btn.classList.contains('has-submenu')) {
          const mainBtn = btn.querySelector('.floating-button');
          if (mainBtn) this._updateIconColorForButton(mainBtn);
          btn
            .querySelectorAll('.floating-submenu .floating-button')
            .forEach((sb) => this._updateIconColorForButton(sb));
        } else {
          this._updateIconColorForButton(btn);
        }
      });
    } catch (e) {
      console.error('Failed refreshing icon colors', e);
    }
  }

  createButton(config, index) {
    // Submenu support: if a config has `children`, render a hoverable wrapper
    if (config && Array.isArray(config.children) && config.children.length) {
      const wrapper = document.createElement('div');
      wrapper.className = 'floating-button-wrapper has-submenu menu-row';
      wrapper.setAttribute('data-button-index', index);

      // Main button (acts as label/trigger)
      const mainBtn = document.createElement('button');
      mainBtn.className = `floating-button ${config.className || ''}`;
      if (!mainBtn.className.includes('btn-text-control')) {
        mainBtn.classList.add('menu-item');
      }

      if (config.svg) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'button-icon';
        const img = document.createElement('img');
        img.className = 'inject-me no-fill';
        img.setAttribute('data-src', config.svg);
        iconWrap.appendChild(img);
        mainBtn.appendChild(iconWrap);
        mainBtn.classList.add('has-icon');
      } else if (config.inlineSvg) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'button-icon';
        try {
          iconWrap.innerHTML = config.inlineSvg;
          const svg = iconWrap.querySelector('svg');
          if (svg) svg.setAttribute('data-preserve-style', '');
        } catch (e) {
          console.error('Failed inserting inlineSvg for main button', e);
        }
        mainBtn.appendChild(iconWrap);
        mainBtn.classList.add('has-icon');
      } else if (config.icon) {
        const icon = document.createElement('span');
        icon.className = 'button-icon';
        icon.textContent = config.icon;
        mainBtn.appendChild(icon);
        mainBtn.classList.add('has-icon');
      }

      if (config.text) {
        const text = document.createElement('span');
        text.className = 'button-text';
        text.textContent = config.text;
        mainBtn.appendChild(text);
      }

      // Right arrow indicator for submenu (optional)
      if (!config.hideArrow) {
        const spacer = document.createElement('span');
        spacer.className = 'button-spacer';
        mainBtn.appendChild(spacer);
        const arrow = document.createElement('span');
        arrow.className = 'button-arrow';
        const arrowImg = document.createElement('img');
        arrowImg.className = 'inject-me no-fill';
        arrowImg.setAttribute('data-src', '/assets/svg/freccetta.svg');
        arrow.appendChild(arrowImg);
        mainBtn.appendChild(arrow);
      }

      // Allow optional primary click behavior if provided
      if (config.onClick || config.navigateTo) {
        mainBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleButtonClick(index, config, e);
        });
      }

      // Hover color updates for main button icon
      mainBtn.addEventListener('mouseenter', () => {
        mainBtn.classList.add('hover');
        this._updateIconColorForButton(mainBtn);
      });
      mainBtn.addEventListener('mouseleave', () => {
        mainBtn.classList.remove('hover');
        this._updateIconColorForButton(mainBtn);
      });

      // Submenu container
      const submenu = document.createElement('div');
      submenu.className = 'floating-submenu';
      const submenuCapTop = document.createElement('div');
      submenuCapTop.className = 'floating-menu-cap cap-top';
      submenu.appendChild(submenuCapTop);

      // Build submenu items
      const subButtons = [];
      const setDisabled = (btn, disabled) => {
        if (!btn) return;
        // Optional: completely hide items flagged as hide-when-disabled
        if (btn.classList && btn.classList.contains('hide-when-disabled')) {
          btn.style.display = disabled ? 'none' : '';
        }
        if (disabled) {
          btn.classList.add('disabled');
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.classList.remove('disabled');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }
      };

      const updateSubmenuEnablement = () => {
        try {
          const target = this.canvasObject;
          config.children.forEach((child, i) => {
            const btn = subButtons[i];
            if (!btn) return;
            let enabled = true;
            if (typeof child.isEnabled === 'function') {
              enabled = !!child.isEnabled(target, this.canvas, this);
            } else if (typeof child.disabled !== 'undefined') {
              enabled = !child.disabled;
            }
            setDisabled(btn, !enabled);
          });
        } catch (e) {
          console.error('Failed updating submenu enablement', e);
        }
      };

      // Track currently open nested submenu within this submenu
      let openNested = null;
      let openBtn = null;
      let openHideTimeout = null;

      config.children.forEach((child, i) => {
        // Support custom-rendered control rows at first-level submenu
        if (
          child &&
          child.type === 'control' &&
          typeof child.render === 'function'
        ) {
          const cWrap = document.createElement('div');
          cWrap.className = `floating-control ${child.className || ''}`;
          try {
            const content = child.render(
              cWrap,
              this.canvasObject,
              this.canvas,
              this
            );
            if (content && content !== cWrap) cWrap.appendChild(content);
          } catch (err) {
            console.error('control render failed', err);
          }
          submenu.appendChild(cWrap);
          // Maintain index alignment for enablement logic
          subButtons[i] = null;
          return; // skip button creation for control rows
        }

        // Support divider rows inside first-level submenu
        if (child && child.type === 'divider') {
          const divider = document.createElement('div');
          divider.className = 'floating-divider';
          submenu.appendChild(divider);
          subButtons[i] = null;
          return;
        }

        const subBtn = document.createElement('button');
        subBtn.className = `floating-button floating-submenu-item ${
          child.className || ''
        }`;
        if (!subBtn.className.includes('btn-text-control')) {
          subBtn.classList.add('menu-item');
        }

        if (child.svg) {
          const iconWrap = document.createElement('span');
          iconWrap.className = 'button-icon';
          const img = document.createElement('img');
          img.className = 'inject-me no-fill';
          img.setAttribute('data-src', child.svg);
          iconWrap.appendChild(img);
          subBtn.appendChild(iconWrap);
          subBtn.classList.add('has-icon');
        } else if (child.inlineSvg) {
          const iconWrap = document.createElement('span');
          iconWrap.className = 'button-icon';
          try {
            iconWrap.innerHTML = child.inlineSvg;
            const svg = iconWrap.querySelector('svg');
            if (svg) svg.setAttribute('data-preserve-style', '');
          } catch (e) {
            console.error('Failed inserting inlineSvg for submenu item', e);
          }
          subBtn.appendChild(iconWrap);
          subBtn.classList.add('has-icon');
        } else if (child.icon) {
          const icon = document.createElement('span');
          icon.className = 'button-icon';
          icon.textContent = child.icon;
          subBtn.appendChild(icon);
          subBtn.classList.add('has-icon');
        }

        if (child.text) {
          const text = document.createElement('span');
          text.className = 'button-text';
          text.textContent = child.text;
          subBtn.appendChild(text);
        }

        // If this submenu item has its own children, add arrow and nested submenu
        let nested;
        if (Array.isArray(child.children) && child.children.length) {
          const spacer = document.createElement('span');
          spacer.className = 'button-spacer';
          subBtn.appendChild(spacer);
          const arrow = document.createElement('span');
          arrow.className = 'button-arrow';
          const arrowImg = document.createElement('img');
          arrowImg.className = 'inject-me no-fill';
          arrowImg.setAttribute('data-src', '/assets/svg/freccetta.svg');
          arrow.appendChild(arrowImg);
          subBtn.appendChild(arrow);

          nested = document.createElement('div');
          nested.className = 'floating-submenu';
          const nestedCapTop = document.createElement('div');
          nestedCapTop.className = 'floating-menu-cap cap-top';
          nested.appendChild(nestedCapTop);

          // Build nested submenu items (one more level)
          child.children.forEach((grand) => {
            // Support custom-rendered control rows
            if (
              grand &&
              grand.type === 'control' &&
              typeof grand.render === 'function'
            ) {
              const gWrap = document.createElement('div');
              gWrap.className = `floating-control ${grand.className || ''}`;
              try {
                const content = grand.render(
                  gWrap,
                  this.canvasObject,
                  this.canvas,
                  this
                );
                if (content && content !== gWrap) gWrap.appendChild(content);
              } catch (err) {
                console.error('nested control render failed', err);
              }
              nested.appendChild(gWrap);
              return; // skip default button behavior
            }

            // Support divider rows inside nested submenu
            if (grand && grand.type === 'divider') {
              const nDiv = document.createElement('div');
              nDiv.className = 'floating-divider';
              nested.appendChild(nDiv);
              return;
            }

            // Support preview rows (non-interactive) inside nested submenu
            if (grand && grand.type === 'preview') {
              const pBtn = document.createElement('button');
              pBtn.className =
                'floating-button floating-submenu-item ai-feature-preview';
              pBtn.setAttribute('aria-disabled', 'true');
              pBtn.style.cursor = 'default';
              try {
                const img = document.createElement('img');
                img.className = 'ai-feature-preview-img';
                img.alt = grand.alt || 'Anteprima';
                img.src = grand.src || grand.image || '';
                pBtn.appendChild(img);
              } catch (e) {
                console.error(
                  'Failed closing previously open nested submenu',
                  e
                );
              }
              nested.appendChild(pBtn);
              return;
            }

            const gBtn = document.createElement('button');
            gBtn.className = `floating-button floating-submenu-item ${
              grand.className || ''
            }`;
            if (!gBtn.className.includes('btn-text-control')) {
              gBtn.classList.add('menu-item');
            }
            if (grand.svg) {
              const iconWrap = document.createElement('span');
              iconWrap.className = 'button-icon';
              const img = document.createElement('img');
              img.className = 'inject-me no-fill';
              img.setAttribute('data-src', grand.svg);
              iconWrap.appendChild(img);
              gBtn.appendChild(iconWrap);
            } else if (grand.icon) {
              const icon = document.createElement('span');
              icon.className = 'button-icon';
              icon.textContent = grand.icon;
              gBtn.appendChild(icon);
            }
            if (grand.text) {
              const t = document.createElement('span');
              t.className = 'button-text';
              t.textContent = grand.text;
              gBtn.appendChild(t);
            }

            gBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              if (gBtn.classList.contains('disabled')) return;
              try {
                if (typeof grand.onClick === 'function') {
                  grand.onClick(e, this.canvasObject, this);
                }
                if (grand.navigateTo) {
                  this.navigateTo(grand.navigateTo);
                }
              } catch (err) {
                console.error('nested submenu item failed', err);
              }
            });

            gBtn.addEventListener('mouseenter', () => {
              gBtn.classList.add('hover');
              this._updateIconColorForButton(gBtn);
            });
            gBtn.addEventListener('mouseleave', () => {
              gBtn.classList.remove('hover');
              this._updateIconColorForButton(gBtn);
            });

            nested.appendChild(gBtn);
          });
          const nestedCapBottom = document.createElement('div');
          nestedCapBottom.className = 'floating-menu-cap cap-bottom';
          nested.appendChild(nestedCapBottom);

          // Hover handlers for nested submenu positioning
          let nhTimeout;
          const nShow = () => {
            // Close any previously open nested immediately to avoid multiple parallel panels
            if (openHideTimeout) {
              clearTimeout(openHideTimeout);
              openHideTimeout = null;
            }
            if (openNested && openNested !== nested) {
              try {
                openNested.style.display = 'none';
                if (openBtn) openBtn.classList.remove('submenu-open');
              } catch (e) {
                void e;
              }
            }
            openNested = nested;
            openBtn = subBtn;
            clearTimeout(nhTimeout);
            nested.style.visibility = 'hidden';
            nested.style.display = 'flex';
            nested.style.left = 'calc(100% + var(--submenu-gap, 12px))';
            nested.style.right = 'auto';
            nested.style.top = '0px';
            nested.style.transform = '';
            // Keep this submenu item visually open while nested is shown
            subBtn.classList.add('submenu-open');
            const viewportW =
              window.innerWidth || document.documentElement.clientWidth;
            const viewportH =
              window.innerHeight || document.documentElement.clientHeight;
            const btnRect = subBtn.getBoundingClientRect();
            const nRect = nested.getBoundingClientRect();
            const overflowRight = btnRect.right + nRect.width > viewportW - 8;
            if (overflowRight) {
              nested.style.left = 'auto';
              nested.style.right = 'calc(100% + var(--submenu-gap, 12px))';
            }
            let offsetY = 0;
            const nTop = btnRect.top;
            const nBottom = nTop + nRect.height;
            const overflowBottom = nBottom - (viewportH - 8);
            if (overflowBottom > 0) offsetY -= overflowBottom;
            const overflowTop = 8 - nTop + offsetY;
            if (overflowTop > 0) offsetY += overflowTop;
            if (offsetY !== 0)
              nested.style.transform = `translateY(${offsetY}px)`;
            nested.style.visibility = 'visible';
          };
          const nHide = () => {
            nhTimeout = setTimeout(() => {
              // Delay hide to allow moving pointer into the nested panel
              nested.style.display = 'none';
              subBtn.classList.remove('submenu-open');
              if (openNested === nested) {
                openNested = null;
                openBtn = null;
              }
            }, 120);
          };
          // Ensure nested submenu positions relative to this item
          try {
            subBtn.style.position = 'relative';
          } catch (e) {
            console.error('Failed positioning nested submenu', e);
          }

          subBtn.addEventListener('mouseenter', nShow);
          subBtn.addEventListener('mouseleave', nHide);
          // Keep nested open when hovering it
          nested.addEventListener('mouseenter', () => {
            clearTimeout(nhTimeout);
          });
          nested.addEventListener('mouseleave', () => {
            // Hide with slight delay; if moving to another item, its nShow will close this immediately
            if (openHideTimeout) clearTimeout(openHideTimeout);
            openHideTimeout = setTimeout(() => {
              nHide();
            }, 40);
          });
          subBtn.appendChild(nested);
        }

        subBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          // If disabled, ignore clicks
          if (subBtn.classList.contains('disabled')) return;
          try {
            if (typeof child.onClick === 'function') {
              child.onClick(e, this.canvasObject, this);
            }
            if (child.navigateTo) {
              this.navigateTo(child.navigateTo);
            }
          } catch (err) {
            console.error('submenu item failed', err);
          }
          // Re-evaluate enablement after action (z-order may have changed)
          setTimeout(updateSubmenuEnablement, 0);
        });

        // Hover color updates for submenu item icons
        subBtn.addEventListener('mouseenter', () => {
          subBtn.classList.add('hover');
          this._updateIconColorForButton(subBtn);
        });
        subBtn.addEventListener('mouseleave', () => {
          subBtn.classList.remove('hover');
          this._updateIconColorForButton(subBtn);
        });

        submenu.appendChild(subBtn);
        subButtons.push(subBtn);
      });

      const submenuCapBottom = document.createElement('div');
      submenuCapBottom.className = 'floating-menu-cap cap-bottom';
      submenu.appendChild(submenuCapBottom);

      // Hover interactions
      let hoverTimeout;
      const show = () => {
        clearTimeout(hoverTimeout);
        // Ensure only one first-level submenu is visible at a time across
        // this FloatingControls instance. Close any other open sibling submenus
        // immediately so they don't stack while their hide delay elapses.
        try {
          const all = this.element.querySelectorAll(
            '.floating-button-wrapper.has-submenu > .floating-submenu'
          );
          all.forEach((el) => {
            if (el !== submenu && el.style.display !== 'none') {
              el.style.display = 'none';
              const parent = el.parentElement;
              if (parent) {
                const prevMain = parent.querySelector('.floating-button');
                if (prevMain) prevMain.classList.remove('submenu-open');
              }
            }
          });
          this._openTopSubmenu = submenu;
        } catch (e) {
          console.error('Failed enforcing single open submenu', e);
        }
        updateSubmenuEnablement();
        // Show to measure
        submenu.style.visibility = 'hidden';
        submenu.style.display = 'flex';
        submenu.style.left = 'calc(100% + var(--submenu-gap, 12px))';
        submenu.style.right = 'auto';
        submenu.style.top = '0px';
        submenu.style.transform = '';
        // Keep parent visually open to avoid flicker at rounded corners
        mainBtn.classList.add('submenu-open');

        const viewportW =
          window.innerWidth || document.documentElement.clientWidth;
        const viewportH =
          window.innerHeight || document.documentElement.clientHeight;
        const wrapRect = wrapper.getBoundingClientRect();
        const subRect = submenu.getBoundingClientRect();

        // Horizontal flip if overflowing right edge
        const overflowRight = wrapRect.right + subRect.width > viewportW - 8; // 8px tolerance
        if (overflowRight) {
          submenu.style.left = 'auto';
          submenu.style.right = 'calc(100% + var(--submenu-gap, 12px))';
        }

        // Vertical adjust to keep submenu inside viewport
        let offsetY = 0;
        const subTop = wrapRect.top; // submenu anchors at wrapper top
        const subBottom = subTop + subRect.height;
        const overflowBottom = subBottom - (viewportH - 8);
        if (overflowBottom > 0) {
          offsetY -= overflowBottom;
        }
        const overflowTop = 8 - subTop + offsetY;
        if (overflowTop > 0) {
          offsetY += overflowTop; // push down if it goes above
        }
        if (offsetY !== 0) {
          submenu.style.transform = `translateY(${offsetY}px)`;
        }

        // Finally make visible
        submenu.style.visibility = 'visible';
      };
      const hide = () => {
        hoverTimeout = setTimeout(() => {
          // Do not hide the first-level submenu if a nested submenu
          // is currently open for one of its items. This prevents
          // the panel from disappearing while the user moves the
          // pointer into the preview/tooltip area on the right.
          try {
            const anyNestedOpen =
              !!openNested && openNested.style.display !== 'none';
            if (anyNestedOpen) return;
          } catch (e) {
            console.error('Failed preventing submenu hide while hovered', e);
          }
          submenu.style.display = 'none';
          mainBtn.classList.remove('submenu-open');
          if (this._openTopSubmenu === submenu) {
            this._openTopSubmenu = null;
          }
        }, 100);
      };
      wrapper.addEventListener('mouseenter', show);
      wrapper.addEventListener('mouseleave', hide);

      wrapper.appendChild(mainBtn);
      wrapper.appendChild(submenu);

      // Ensure SVG injection runs later
      try {
        const { injectSvgIcons } = require('~src/ui');
        if (typeof injectSvgIcons === 'function') setTimeout(injectSvgIcons, 0);
      } catch (e) {
        console.error('Failed computing submenu show/hide', e);
      }

      return wrapper;
    }

    if (config && config.type === 'container') {
      const container = document.createElement('div');
      container.className = config.className || '';
      if (config.id) container.id = config.id;
      container.setAttribute('data-button-index', index);
      return container;
    }
    // Support non-button items like dividers
    if (config && config.type === 'divider') {
      const divider = document.createElement('div');
      divider.className = 'floating-divider';
      // Using data-index to keep consistent indexing if needed
      divider.setAttribute('data-button-index', index);
      return divider;
    }

    const button = document.createElement('button');
    button.className = `floating-button ${config.className || ''}`;
    if (!button.className.includes('btn-text-control')) {
      button.classList.add('menu-item');
    }
    // Mark top-level row for first/last rounding logic
    button.classList.add('menu-row');
    button.setAttribute('data-button-index', index);

    if (config.svg) {
      const iconWrap = document.createElement('span');
      iconWrap.className = 'button-icon';
      const img = document.createElement('img');
      img.className = 'inject-me no-fill';
      img.setAttribute('data-src', config.svg);
      iconWrap.appendChild(img);
      button.appendChild(iconWrap);
      button.classList.add('has-icon');
    } else if (config.inlineSvg) {
      const iconWrap = document.createElement('span');
      iconWrap.className = 'button-icon';
      try {
        iconWrap.innerHTML = config.inlineSvg;
        const svg = iconWrap.querySelector('svg');
        if (svg) svg.setAttribute('data-preserve-style', '');
      } catch (e) {
        console.error('Failed inserting inlineSvg for button', e);
      }
      button.appendChild(iconWrap);
      button.classList.add('has-icon');
    } else if (config.icon) {
      const icon = document.createElement('span');
      icon.className = 'button-icon';
      icon.textContent = config.icon;
      button.appendChild(icon);
      button.classList.add('has-icon');
    }

    if (config.text) {
      const text = document.createElement('span');
      text.className = 'button-text';
      text.textContent = config.text;
      button.appendChild(text);
    }

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleButtonClick(index, config, e);
    });

    button.addEventListener('mouseenter', () => {
      button.classList.add('hover');
      this._updateIconColorForButton(button);
    });

    button.addEventListener('mouseleave', () => {
      button.classList.remove('hover');
      this._updateIconColorForButton(button);
    });

    return button;
  }

  handleButtonClick(index, config, event) {
    // Navigation takes precedence if provided
    if (config.navigateTo) {
      this.navigateTo(config.navigateTo);
      // Notify listeners about navigation so providers can react (e.g., populate UI)
      try {
        this.handleClick({
          ...event,
          type: 'navigate',
          viewId: config.navigateTo,
          buttonIndex: index,
          buttonConfig: config,
        });
      } catch (e) {
        console.error('Failed injecting icons after renderActiveView', e);
      }
      return;
    }

    if (config.onClick) {
      config.onClick(event, this.canvasObject, this);
    }

    this.handleClick({ ...event, buttonIndex: index, buttonConfig: config });
  }

  updateButton(index, updates) {
    const view = this.options.views[this.activeView];
    if (!view) return;
    const btnEl = this._activeViewButtonsDom[index];
    const config = view.buttons[index];
    if (!config) return;

    if (updates.text !== undefined) {
      if (btnEl) {
        const textElement = btnEl.querySelector('.button-text');
        if (textElement) {
          textElement.textContent = updates.text;
        }
      }
      config.text = updates.text;
    }

    if (updates.icon !== undefined) {
      if (btnEl) {
        // If there's an SVG inside, replace it with text icon
        const iconWrap = btnEl.querySelector('.button-icon');
        if (iconWrap) {
          iconWrap.innerHTML = '';
          const iconSpan = document.createElement('span');
          iconSpan.textContent = updates.icon;
          iconWrap.appendChild(iconSpan);
        }
      }
      config.icon = updates.icon;
    }

    if (updates.svg !== undefined) {
      if (btnEl) {
        let iconWrap = btnEl.querySelector('.button-icon');
        if (!iconWrap) {
          iconWrap = document.createElement('span');
          iconWrap.className = 'button-icon';
          btnEl.insertBefore(iconWrap, btnEl.firstChild);
        }
        iconWrap.innerHTML = '';
        const img = document.createElement('img');
        img.className = 'inject-me no-fill';
        img.setAttribute('data-src', updates.svg);
        iconWrap.appendChild(img);
        try {
          const { injectSvgIcons } = require('~src/ui');
          if (typeof injectSvgIcons === 'function') injectSvgIcons();
        } catch (e) {
          console.error('Failed injecting icon on updateButton', e);
        }
        btnEl.classList.add('has-icon');
      }
      // Keep only one of svg/icon in config
      delete config.icon;
      config.svg = updates.svg;
    }

    if (updates.className !== undefined) {
      if (btnEl) {
        btnEl.className = `floating-button ${updates.className || ''}`;
      }
      config.className = updates.className;
    }

    if (updates.onClick !== undefined) {
      config.onClick = updates.onClick;
    }
  }

  addButton(config, viewId = this.activeView) {
    const view = this.options.views[viewId];
    if (!view) return -1;
    const idx = view.buttons.length;
    view.buttons.push(config);
    if (viewId === this.activeView) {
      const inner = this.element.querySelector('.floating-controls-inner');
      const button = this.createButton(config, idx);
      this._activeViewButtonsDom.push(button);
      inner && inner.appendChild(button);
    }
    return idx;
  }

  removeButton(index, viewId = this.activeView) {
    const view = this.options.views[viewId];
    if (!view) return;
    if (viewId === this.activeView) {
      const btn = this._activeViewButtonsDom[index];
      if (btn) btn.remove();
      this._activeViewButtonsDom.splice(index, 1);
      // Reindex
      this._activeViewButtonsDom.forEach((el, i) =>
        el.setAttribute('data-button-index', i)
      );
    }
    view.buttons.splice(index, 1);
  }

  setEditMode(isEdit) {
    this.isEditMode = isEdit;
    this.element.classList.toggle('edit-mode', isEdit);

    // Update reposition button in all views (configs) and active view DOM
    const updateForView = (viewId) => {
      const view = this.options.views[viewId];
      if (!view) return;
      const idx = view.buttons.findIndex(
        (b) =>
          b?.role === 'reposition' ||
          (b.className || '').includes('btn-reposition') ||
          (b.className || '').includes('btn-edit')
      );
      if (idx !== -1) {
        // Update config
        view.buttons[idx] = {
          ...view.buttons[idx],
          text: isEdit
            ? 'Termina Riposizionamento'
            : 'Riposiziona Foto Defunto',
          icon: isEdit ? '✓' : '🎯',
          // Keep same style as "Sostituisci Foto Defunto"
          className: 'btn-customize',
        };
        // Update DOM only if active view
        if (viewId === this.activeView) {
          this.updateButton(idx, {
            text: view.buttons[idx].text,
            icon: view.buttons[idx].icon,
            className: view.buttons[idx].className,
          });
        }
      }
    };

    Object.keys(this.options.views).forEach(updateForView);
  }

  navigateTo(viewId) {
    if (!this.options.views[viewId]) return;
    this.activeView = viewId;
    this.renderActiveView();
  }

  renderActiveView() {
    const header = this.element.querySelector('.floating-controls-header');
    const inner = this.element.querySelector('.floating-controls-inner');
    const footer = this.element.querySelector('.floating-controls-footer');
    if (!header || !inner || !footer) return;

    // Clear containers
    header.innerHTML = '';
    inner.innerHTML = '';
    footer.innerHTML = '';
    this._activeViewButtonsDom = [];

    const view = this.options.views[this.activeView];
    if (!view) return;

    // Header: back + title
    const shouldShowBack =
      view.showBack !== false && this.activeView !== 'home';
    const hasTitle = !!(view.title && String(view.title).trim());
    if (hasTitle) {
      const titleEl = document.createElement('div');
      titleEl.className = 'floating-controls-title';
      titleEl.textContent = view.title;
      header.appendChild(titleEl);
    }

    // Add back button into footer (bottom-left)
    if (shouldShowBack) {
      const backBtn = document.createElement('button');
      backBtn.className = 'floating-back-button';
      backBtn.textContent = '↩ Indietro';
      backBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = view.backTo || 'home';
        this.navigateTo(target);
      });
      footer.appendChild(backBtn);
    }

    // Toggle header/footer visibility based on content
    header.style.display = hasTitle ? 'flex' : 'none';
    footer.style.display = shouldShowBack ? 'flex' : 'none';

    // Adjust layout (row vs column)
    const isRowLayout = view.layout === 'row';
    inner.classList.toggle('layout-row', isRowLayout);

    const fragment = document.createDocumentFragment();
    let hasMenuRows = false;

    // Buttons for this view
    view.buttons.forEach((buttonConfig, index) => {
      const button = this.createButton(buttonConfig, index);
      this._activeViewButtonsDom.push(button);
      if (button && button.classList && button.classList.contains('menu-row')) {
        hasMenuRows = true;
      }
      fragment.appendChild(button);
    });

    if (!isRowLayout && hasMenuRows) {
      const topCap = document.createElement('div');
      topCap.className = 'floating-menu-cap cap-top';
      inner.appendChild(topCap);
      inner.appendChild(fragment);
      const bottomCap = document.createElement('div');
      bottomCap.className = 'floating-menu-cap cap-bottom';
      inner.appendChild(bottomCap);
    } else {
      inner.appendChild(fragment);
    }

    // Inject SVG icons for any newly added controls
    try {
      const { injectSvgIcons } = require('~src/ui');
      if (typeof injectSvgIcons === 'function') {
        injectSvgIcons();
      }
    } catch (e) {
      console.error('Failed reinjecting icons after renderActiveView', e);
    }
    // Ensure icon colors are correct after injection
    setTimeout(() => this.refreshIconColors(), 0);
  }

  destroy() {
    this._activeViewButtonsDom = [];
    super.destroy();
  }
}
